import { localizedStrings } from '@core/strings'
import React, { useState } from 'react'
import { alpha, styled, useThemeProps } from '@mui/material/styles'
import { Box, Flex, Text } from '@fundamentals'
import {
  CircularProgress,
  Menu,
  MenuItem,
  Divider,
  Tooltip,
} from '@common/material'
import { useRouter } from 'next/router'
import { OrganisationsMenuProps } from './types'
import {
  ExpandMore,
  ExpandLess,
  SettingsOutlined,
  Logout,
  SwapHoriz,
  Language,
} from '@mui/icons-material'
import { PageUrls } from '@core/page-urls'
import { useGetProfile } from '@core/react-query/features/profile/hooks/useGetProfile'
import { useGetOrganisation } from '@core/react-query/features/organisations/organisation/hooks/useGetOrganisation'
import { useIntercom } from 'react-use-intercom'
import { useModal } from '@common/GlobalModal'
import { UserAvatar } from '@common/UserAvatar'
import { SwitchOrganisationModal } from './SwitchOrganisationModal'
import { Typography } from '@mui/material'
import { Palette } from '@core/theme/color-palette-siteassist'
import { useNavigationContext } from '@common/hooks/useNavigationContext'

export const StyledOrganisationButton = styled(Flex)`
  align-items: center;
  height: 48px;
  ${({ theme }) =>
    theme.unstable_sx({
      mx: 1,
      py: 1,
    })};
`

const OrganisationButtonInner = styled(Flex)`
  cursor: pointer;
  align-items: center;
  border-radius: 4px;
  padding: 4px 8px;
  ${({ theme }) =>
    theme.unstable_sx({
      '&:hover': {
        bgcolor: alpha('#000', 0.04),
      },
    })};
`

const OrganisationMenu: React.FC<OrganisationsMenuProps> = ({
  organisationId,
  collapse = false,
}) => {
  const { menuItemSx } = useThemeProps({
    name: 'Sidebar',
    props: null,
  })
  const { data: profile } = useGetProfile()
  const { data: organisation } = useGetOrganisation(
    { organisationId },
    { enabled: !!organisationId },
  )
  const isSuperUser = profile?.isSuperUser() ?? false
  const canSwitchOrganisation =
    isSuperUser || profile?.organisations?.length > 1
  const router = useRouter()
  const { shutdown } = useIntercom()
  const { showModal } = useModal()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const {
    inSupportContext,
    inProjectSettingsContext,
    inOrganisationSettingsContext,
  } = useNavigationContext()
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleSwitchOrganisation = () => {
    handleClose()
    showModal(
      <SwitchOrganisationModal
        organisations={profile?.organisations || []}
        currentOrganisationId={organisationId}
        isSuperUser={isSuperUser}
      />,
    )
  }

  const onProfileClick = () => {
    router.push(
      PageUrls.organisationUserProfile({
        organisationId: organisationId,
        userId: profile.id,
      }),
    )
    handleClose()
  }

  const handleLogout = () => {
    API.logout()
    shutdown()
    handleClose()
  }

  const handleOrganisationSettings = () => {
    router.push(PageUrls.organisationSettings({ organisationId }))
    handleClose()
  }

  const handleBackToSupport = () => {
    router.push(PageUrls.adminOrganisations)
    handleClose()
  }

  const showBackToSupport =
    profile?.isSuperUser() &&
    !inSupportContext &&
    !inProjectSettingsContext &&
    !inOrganisationSettingsContext

  const showOrganisationSettings = !!organisationId && !!organisation?.canEdit()
  const showMiddleSection =
    showOrganisationSettings || canSwitchOrganisation || showBackToSupport

  return (
    <>
      <StyledOrganisationButton>
        <OrganisationButtonInner
          onClick={handleClick}
          data-test='organisation-menu'
          sx={{ px: 0.75, width: '100%' }}
        >
          <Flex
            alignItems='center'
            justifyContent='center'
            sx={{
              width: 20,
              height: 20,
              borderRadius: '6px',
              backgroundColor: Palette.BlueGrey[400],
              flexShrink: 0,
            }}
          >
            <Typography
              fontSize={14}
              fontWeight={400}
              lineHeight='100%'
              sx={{ color: 'primary.contrastText' }}
            >
              {organisation?.name?.[0] ?? 'S'}
            </Typography>
          </Flex>
          {!collapse && (
            <>
              <Flex flexDirection='column' justifyContent='center' minWidth={0}>
                {!profile ? (
                  <CircularProgress size={16} sx={{ color: 'common.white' }} />
                ) : (
                  <Tooltip
                    title={organisation?.name ?? localizedStrings.siteAssist}
                    placement='bottom'
                  >
                    <Text
                      pl={0.75}
                      fontSize={14}
                      fontWeight={500}
                      lineHeight='1.2'
                      color={Palette.Grey[800]}
                      sx={{
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {organisation?.name ?? localizedStrings.siteAssist}
                    </Text>
                  </Tooltip>
                )}
              </Flex>
              {open ? (
                <ExpandLess sx={{ ml: 0.5, fontSize: 18, flexShrink: 0 }} />
              ) : (
                <ExpandMore sx={{ ml: 0.5, fontSize: 18, flexShrink: 0 }} />
              )}
            </>
          )}
        </OrganisationButtonInner>
      </StyledOrganisationButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        marginThreshold={0}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{
          '& .MuiMenu-list': {
            p: 0,
          },
        }}
        slotProps={{
          paper: {
            sx: {
              p: 0.5,
              mt: 0.5,
              borderRadius: '8px',
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: `0px 4px 16px ${alpha('#000', 0.08)}`,
            },
          },
        }}
      >
        {/* User Info Section */}
        <Box
          onClick={onProfileClick}
          data-test='view-profile'
          sx={{
            px: 1,
            py: 1.5,
            cursor: 'pointer',
            '&:hover': {
              bgcolor: 'action.hover',
            },
          }}
        >
          <Flex alignItems='center' gap={1.5}>
            <UserAvatar
              user={profile}
              sx={{
                width: 32,
                height: 32,
              }}
            />
            <Box>
              <Text fontSize={14} fontWeight={600}>
                {profile?.firstName} {profile?.lastName}
              </Text>
              <Text fontSize={13} color='text.secondary'>
                {profile?.email}
              </Text>
            </Box>
          </Flex>
        </Box>

        <Divider sx={{ my: 0.5, mx: 1, borderBottomWidth: '0.5px' }} />

        {showOrganisationSettings && (
          <MenuItem
            onClick={handleOrganisationSettings}
            data-test='organisation-settings'
            sx={{ ...menuItemSx, fontSize: '13px' }}
          >
            <SettingsOutlined sx={{ width: 16, height: 16 }} color='action' />
            {localizedStrings.organisationSettings}
          </MenuItem>
        )}

        {canSwitchOrganisation && (
          <MenuItem
            onClick={handleSwitchOrganisation}
            sx={{ ...menuItemSx, fontSize: '13px' }}
          >
            <SwapHoriz sx={{ width: 16, height: 16 }} color='action' />
            {localizedStrings.switchOrganisation}
          </MenuItem>
        )}

        {showBackToSupport && (
          <MenuItem
            onClick={handleBackToSupport}
            sx={{ ...menuItemSx, fontSize: '13px' }}
          >
            <Language sx={{ width: 16, height: 16 }} color='action' />
            {localizedStrings.supportView}
          </MenuItem>
        )}

        {showMiddleSection && (
          <Box sx={{ py: 0.5 }}>
            <Divider sx={{ mx: 1, borderBottomWidth: '0.5px' }} />
          </Box>
        )}

        <MenuItem
          onClick={handleLogout}
          sx={{ ...menuItemSx, fontSize: '13px' }}
        >
          <Logout sx={{ width: 16, height: 16 }} color='action' />
          {localizedStrings.logout}
        </MenuItem>
      </Menu>
    </>
  )
}

export { OrganisationMenu }
